import { ReactNode } from 'react';
import SharedReviewLayoutItem, {
  ReviewSectionItem,
} from './shared-review-layout-item';
import SharedCard from './shared-card';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export interface ReviewSection {
  title: string;
  headerButton?: ReactNode;
  leftWidth?: {
    columnWidth?: number;
    labelWidth?: number;
    valueWidth?: number;
  };
  rightWidth?: {
    columnWidth?: number;
    labelWidth?: number;
    valueWidth?: number;
  };
  leftItems?: ReviewSectionItem[];
  rightItems?: ReviewSectionItem[];
  height?: number | string;
}

interface SharedReviewLayoutProps {
  leftSections: ReviewSection[];
  rightSections?: ReviewSection[];
  isDrawer?: boolean;
  variant?: 'main' | 'light';
}

export default function SharedReviewLayoutNew({
  leftSections,
  rightSections,
  isDrawer = false,
  variant = 'main',
}: SharedReviewLayoutProps) {
  const theme = useTheme();
  const isSplitLayout = !!leftSections && !!rightSections && !isDrawer;

  const renderSectionCard = (section: ReviewSection) => {
    const hasVisibleItemsLeft =
      section.leftItems?.some((item) => item.isVisible !== false) ?? false;
    const hasVisibleItemRight =
      section.rightItems?.some((item) => item.isVisible !== false) ?? false;
    const hasVisibleItems = hasVisibleItemsLeft || hasVisibleItemRight;
    const gridLg =
      section.leftWidth?.columnWidth ?? (hasVisibleItemRight ? 'grow' : 12);

    if (!hasVisibleItems) return null;

    const cardBgColor =
      variant === 'light'
        ? theme.palette.semantic.surface.bg3
        : theme.palette.semantic.surface.bg1;

    return (
      <SharedCard
        key={section.title}
        sx={
          !isDrawer
            ? {
                flex: section.height ? '0 0 auto' : '1 1 0',
                minHeight: 0,
                height: section.height,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                borderColor: theme.palette.semantic.outline.light,
                backgroundColor: cardBgColor,
                overflow: 'hidden',
              }
            : {
                backgroundColor: cardBgColor,
              }
        }
      >
        <Box
          sx={{
            p: 2,
            pb: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: theme.palette.semantic.text.title2,
            }}
          >
            {section.title}
          </Typography>

          {section.headerButton && <Box>{section.headerButton}</Box>}
        </Box>

        <Box
          sx={{
            p: 2,
            pt: 0,
            width: '100%',
            flex: 1,
            minHeight: 0,
            overflowY: isDrawer ? 'visible' : 'auto',
          }}
        >
          <Grid container spacing={2}>
            {hasVisibleItemsLeft && (
              <Grid
                size={{
                  xs: 12,
                  lg: isDrawer ? 12 : gridLg,
                }}
              >
                <Grid container spacing={1}>
                  {section.leftItems?.map((item, index) => (
                    <SharedReviewLayoutItem
                      key={`${item.label || 'item'}-${index.toString()}`}
                      item={item}
                      hasOpposite={!!hasVisibleItemRight}
                      widths={section.leftWidth}
                    />
                  ))}
                </Grid>
              </Grid>
            )}

            {!isDrawer && hasVisibleItemsLeft && hasVisibleItemRight && (
              <Grid
                size={{ xs: 0.3 }}
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  justifyContent: 'center',
                }}
              >
                <Divider orientation="vertical" flexItem />
              </Grid>
            )}

            {hasVisibleItemRight && (
              <Grid
                size={{
                  xs: 12,
                  lg: isDrawer ? 12 : gridLg,
                }}
              >
                <Grid container spacing={1}>
                  {section.rightItems?.map((item, index) => (
                    <SharedReviewLayoutItem
                      key={`${item.label || 'item'}-${index.toString()}`}
                      item={item}
                      hasOpposite={!!hasVisibleItemsLeft}
                      widths={section.rightWidth}
                    />
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
      </SharedCard>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: isDrawer ? 'auto' : '100%',
        minHeight: 0,
      }}
    >
      {isSplitLayout ? (
        <Grid
          container
          spacing={2}
          sx={{
            height: '100%',
            minHeight: 0,
            alignItems: 'stretch',
          }}
        >
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              gap: 2,
            }}
          >
            {leftSections.map((section) => renderSectionCard(section))}
          </Grid>

          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              gap: 2,
            }}
          >
            {rightSections.map((section) => renderSectionCard(section))}
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            height: isDrawer ? 'auto' : '100%',
            minHeight: 0,
          }}
        >
          {leftSections.map((section) => renderSectionCard(section))}
          {isDrawer &&
            rightSections?.map((section) => renderSectionCard(section))}
        </Box>
      )}
    </Box>
  );
}
